import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const LANGUAGE_TO_LOCALE = {
  english: "en-US",
  hindi: "hi-IN",
  spanish: "es-ES",
  french: "fr-FR",
  german: "de-DE",
  portuguese: "pt-PT",
  italian: "it-IT",
  japanese: "ja-JP",
  korean: "ko-KR",
  chinese: "zh-CN",
  arabic: "ar-SA",
  russian: "ru-RU",
};

const mapSpeechError = (errorCode) => {
  switch (errorCode) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone permission was denied.";
    case "no-speech":
      return "No speech detected. Please try again.";
    case "audio-capture":
      return "No microphone was found for this device.";
    case "network":
      return "Speech recognition hit a network issue.";
    default:
      return "Speech recognition failed. Please try again.";
  }
};

const normalizeText = (value) =>
  (value || "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.!?;:])/g, "$1")
    .trim();

const TRAILING_SENTENCE_REGEX = /[.!?][)"'\]]*$/;

const commonPrefixLength = (a, b) => {
  const minLength = Math.min(a.length, b.length);
  let i = 0;
  while (i < minLength && a[i] === b[i]) i += 1;
  return i;
};

// Tweak these in code to change the assistant voice behavior globally.
// `preferredVoiceURI` should match one of `speechSynthesis.getVoices()[].voiceURI`.
const TTS_ENGINE_CONFIG = {
  preferredVoiceURI: 	'Google US English',
  rate: 1.2,
  pitch: 1.2,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const getSpeechLocale = (languageName) => {
  const normalized = String(languageName || "english").toLowerCase().trim();
  return LANGUAGE_TO_LOCALE[normalized] || "en-US";
};

export const htmlToSpeechText = (htmlString) => {
  if (typeof window === "undefined" || !htmlString) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(htmlString), "text/html");
    doc.querySelectorAll("collapsiblediv, linkpreview, chartrender").forEach((node) => {
      node.remove();
    });
    return normalizeText(doc.body.textContent || "");
  } catch {
    return normalizeText(String(htmlString));
  }
};

const useChatVoice = ({ userInput, setUserInput, userLanguage = "English" }) => {
  const recognitionRef = useRef(null);
  const composedTranscriptRef = useRef("");
  const ignoreRecognitionResultsRef = useRef(false);
  const speechQueueRef = useRef([]);
  const queueRunningRef = useRef(false);
  const streamLastTextRef = useRef("");
  const streamRemainderRef = useRef("");
  const streamKeyRef = useRef("");

  const [isListening, setIsListening] = useState(false);
  const [isTtsEnabled, setIsTtsEnabled] = useState(false);
  const [speechError, setSpeechError] = useState("");
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(
    TTS_ENGINE_CONFIG.preferredVoiceURI || "",
  );
  const speechRate = clamp(TTS_ENGINE_CONFIG.rate, 0.75, 1.5);
  const speechPitch = clamp(TTS_ENGINE_CONFIG.pitch, 0.75, 1.5);

  const speechLocale = useMemo(() => getSpeechLocale(userLanguage), [userLanguage]);

  const SpeechRecognitionCtor = useMemo(() => {
    if (typeof window === "undefined") return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  }, []);

  const isSpeechRecognitionSupported = Boolean(SpeechRecognitionCtor);
  const isSpeechSynthesisSupported = useMemo(() => {
    if (typeof window === "undefined") return false;
    return "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
  }, []);

  const clearSpeechError = useCallback(() => setSpeechError(""), []);

  const resetVoiceInput = useCallback(() => {
    composedTranscriptRef.current = "";
    ignoreRecognitionResultsRef.current = true;
    setUserInput("");
  }, [setUserInput]);

  const resetStreamingSpeech = useCallback(() => {
    streamKeyRef.current = "";
    streamLastTextRef.current = "";
    streamRemainderRef.current = "";
    speechQueueRef.current = [];
    queueRunningRef.current = false;
  }, []);

  const drainSpeechQueue = useCallback(() => {
    if (!isSpeechSynthesisSupported || !isTtsEnabled) return;
    if (queueRunningRef.current) return;

    const nextChunk = normalizeText(speechQueueRef.current.shift() || "");
    if (!nextChunk) return;

    queueRunningRef.current = true;
    const utterance = new SpeechSynthesisUtterance(nextChunk);
    const voices = availableVoices.length
      ? availableVoices
      : window.speechSynthesis.getVoices();
    const preferredVoice = voices.find((voice) => voice.voiceURI === selectedVoiceURI);
    const langPrefix = speechLocale.split("-")[0]?.toLowerCase();
    const fallbackVoice =
      voices.find((voice) => voice?.lang?.toLowerCase?.().startsWith(speechLocale.toLowerCase())) ||
      voices.find((voice) => voice?.lang?.toLowerCase?.().startsWith(langPrefix)) ||
      voices[0];
    const resolvedVoice = preferredVoice || fallbackVoice;

    if (resolvedVoice) {
      utterance.voice = resolvedVoice;
      utterance.lang = resolvedVoice.lang || speechLocale;
    } else {
      utterance.lang = speechLocale;
    }

    utterance.rate = speechRate;
    utterance.pitch = speechPitch;
    utterance.onend = () => {
      queueRunningRef.current = false;
      drainSpeechQueue();
    };
    utterance.onerror = () => {
      queueRunningRef.current = false;
      drainSpeechQueue();
    };

    window.speechSynthesis.speak(utterance);
  }, [
    availableVoices,
    isSpeechSynthesisSupported,
    isTtsEnabled,
    selectedVoiceURI,
    speechLocale,
    speechPitch,
    speechRate,
  ]);

  const enqueueSpeech = useCallback(
    (text) => {
      const normalized = normalizeText(text);
      if (!normalized) return;
      speechQueueRef.current.push(normalized);
      drainSpeechQueue();
    },
    [drainSpeechQueue],
  );

  const stopSpeaking = useCallback(() => {
    if (!isSpeechSynthesisSupported) return;
    resetStreamingSpeech();
    window.speechSynthesis.cancel();
  }, [isSpeechSynthesisSupported, resetStreamingSpeech]);

  const stopListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    try {
      recognition.stop();
    } catch {}
  }, []);

  const speakText = useCallback(
    (text) => {
      if (!isSpeechSynthesisSupported || !isTtsEnabled) return;
      const content = normalizeText(text);
      if (!content) return;

      stopSpeaking();
      enqueueSpeech(content);
    },
    [enqueueSpeech, isSpeechSynthesisSupported, isTtsEnabled, stopSpeaking],
  );

  const speakStreamingText = useCallback(
    (latestText, options = {}) => {
      if (!isSpeechSynthesisSupported || !isTtsEnabled) return;

      const isFinal = Boolean(options.isFinal);
      const nextStreamKey = String(options.streamKey || "default");
      if (streamKeyRef.current !== nextStreamKey) {
        streamKeyRef.current = nextStreamKey;
        streamLastTextRef.current = "";
        streamRemainderRef.current = "";
        speechQueueRef.current = [];
        queueRunningRef.current = false;
        window.speechSynthesis.cancel();
      }
      const normalizedFull = normalizeText(latestText);
      const previousText = streamLastTextRef.current;

      let delta = "";
      if (!normalizedFull) {
        if (isFinal && streamRemainderRef.current) {
          enqueueSpeech(streamRemainderRef.current);
          streamRemainderRef.current = "";
        }
        return;
      }

      if (!previousText) {
        delta = normalizedFull;
      } else if (normalizedFull.startsWith(previousText)) {
        delta = normalizedFull.slice(previousText.length);
      } else {
        // Try to find appended content without restarting speech.
        const sameTailIndex = normalizedFull.lastIndexOf(previousText);
        if (sameTailIndex >= 0) {
          delta = normalizedFull.slice(sameTailIndex + previousText.length);
        } else {
          const prefixLen = commonPrefixLength(previousText, normalizedFull);
          delta = prefixLen > 0 ? normalizedFull.slice(prefixLen) : "";
        }
      }

      streamLastTextRef.current = normalizedFull;

      if (!delta && !isFinal) return;

      let working = normalizeText(`${streamRemainderRef.current} ${delta}`);
      if (!working) {
        if (isFinal && streamRemainderRef.current) {
          enqueueSpeech(streamRemainderRef.current);
          streamRemainderRef.current = "";
        }
        return;
      }

      const parts = working.split(/(?<=[.!?])\s+/).filter(Boolean);
      const completeSegments = [];
      let remainder = "";

      if (parts.length === 0) {
        remainder = working;
      } else if (isFinal || TRAILING_SENTENCE_REGEX.test(working)) {
        completeSegments.push(...parts);
      } else {
        remainder = parts.pop() || "";
        completeSegments.push(...parts);
      }

      if (!isFinal && remainder.length > 120) {
        const splitAt = remainder.lastIndexOf(" ");
        if (splitAt > 40) {
          completeSegments.push(remainder.slice(0, splitAt));
          remainder = remainder.slice(splitAt + 1);
        }
      }

      completeSegments.forEach((segment) => enqueueSpeech(segment));
      streamRemainderRef.current = remainder;

      if (isFinal && streamRemainderRef.current) {
        enqueueSpeech(streamRemainderRef.current);
        streamRemainderRef.current = "";
      }
    },
    [enqueueSpeech, isSpeechSynthesisSupported, isTtsEnabled],
  );

  const toggleTts = useCallback(() => {
    if (!isSpeechSynthesisSupported) {
      setSpeechError("Text-to-speech is not supported in this browser.");
      return;
    }
    setSpeechError("");
    setIsTtsEnabled((prev) => {
      const next = !prev;
      if (!next) stopSpeaking();
      return next;
    });
  }, [isSpeechSynthesisSupported, stopSpeaking]);

  const toggleListening = useCallback(() => {
    if (!isSpeechRecognitionSupported || !recognitionRef.current) {
      setSpeechError("Speech-to-text is not supported in this browser.");
      return;
    }

    if (isListening) {
      stopListening();
      return;
    }

    stopSpeaking();
    setSpeechError("");
    ignoreRecognitionResultsRef.current = false;
    composedTranscriptRef.current = normalizeText(userInput);

    const recognition = recognitionRef.current;
    recognition.lang = speechLocale;
    recognition.continuous = true;
    recognition.interimResults = true;

    try {
      recognition.start();
    } catch (error) {
      if (error?.name === "InvalidStateError") return;
      setSpeechError("Unable to start speech recognition.");
    }
  }, [
    isListening,
    isSpeechRecognitionSupported,
    speechLocale,
    stopListening,
    stopSpeaking,
    userInput,
  ]);

  useEffect(() => {
    if (!isSpeechSynthesisSupported) return undefined;

    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices?.length) return;
      setAvailableVoices(voices);
      if (selectedVoiceURI && voices.some((voice) => voice.voiceURI === selectedVoiceURI)) {
        return;
      }
      const langPrefix = speechLocale.split("-")[0]?.toLowerCase();
      const fallbackVoice =
        voices.find((voice) =>
          voice?.lang?.toLowerCase?.().startsWith(speechLocale.toLowerCase()),
        ) ||
        voices.find((voice) => voice?.lang?.toLowerCase?.().startsWith(langPrefix)) ||
        voices[0];
      if (fallbackVoice?.voiceURI) {
        setSelectedVoiceURI(fallbackVoice.voiceURI);
      }
    };

    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
    };
  }, [isSpeechSynthesisSupported, selectedVoiceURI, speechLocale]);

  useEffect(() => {
    if (!SpeechRecognitionCtor) return undefined;

    const recognition = new SpeechRecognitionCtor();

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      if (ignoreRecognitionResultsRef.current) return;

      let finalChunk = "";
      let interimChunk = "";

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript || "";
        if (!transcript) continue;
        if (result.isFinal) finalChunk += ` ${transcript}`;
        else interimChunk += ` ${transcript}`;
      }

      if (finalChunk) {
        composedTranscriptRef.current = normalizeText(
          `${composedTranscriptRef.current} ${finalChunk}`,
        );
      }

      const draft = normalizeText(`${composedTranscriptRef.current} ${interimChunk}`);
      setUserInput(draft);
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      setSpeechError(mapSpeechError(event?.error));
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.onstart = null;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      try {
        recognition.stop();
      } catch {}
      recognitionRef.current = null;
    };
  }, [SpeechRecognitionCtor, setUserInput]);

  useEffect(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    recognition.lang = speechLocale;
  }, [speechLocale]);

  useEffect(() => {
    if (isTtsEnabled) return;
    stopSpeaking();
  }, [isTtsEnabled, stopSpeaking]);

  return {
    clearSpeechError,
    isListening,
    isSpeechRecognitionSupported,
    isSpeechSynthesisSupported,
    isTtsEnabled,
    resetVoiceInput,
    speakText,
    speakStreamingText,
    speechError,
    stopListening,
    stopSpeaking,
    toggleListening,
    toggleTts,
  };
};

export default useChatVoice;
